import { Component, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActionListItemModel,
  ActionMenuItemAction,
  BreadcrumbsComponent,
  ButtonActionEntity,
  CapitalizePipe,
  ClientCreditNote,
  ClientCreditNoteDraftItem,
  ClientCreditNoteItem,
  ClientCreditNoteService,
  ClientInvoice,
  CopyableTitleComponent,
  DetailTitleService,
  DocumentSections,
  DocumentWrapperComponent,
  Entity,
  errorToast,
  FormErrorService,
  ListContext,
  NavigationService,
  OverviewCreateButtonComponent,
  resolveStatusEntity,
  RouteContextMenuService,
  RouteEntitySubscriberService,
  StoredSupplierCreditNoteItemLink,
  successSaveToast,
  SUPPLIER_CREDIT_NOTE_STATUS_CONFIG,
  SupplierCreditNote,
  SupplierCreditNoteItem,
  SupplierCreditNoteItemMapping,
  SupplierCreditNoteMappingType,
  SupplierCreditNoteService,
  SupplierCreditNoteStatus,
  SupplierInvoice,
  TableConfig,
  TableModalComponent,
  ValidationDirective,
} from 'shared';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  catchError,
  concatMap,
  debounceTime,
  EMPTY,
  filter,
  from,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { FormFieldComponent, TextAreaComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { SupplierCreditNoteItemComponent } from '@features/purchase/supplier_credit_notes/supplier-credit-note-details/supplier-credit-note-item/supplier-credit-note-item.component';
import { SupplierCreditNoteTotalsAndTaxesComponent } from '@features/purchase/supplier_credit_notes/supplier-credit-note-details/supplier-credit-note-totals-and-taxes/supplier-credit-note-totals-and-taxes.component';
import { SupplierCreditNoteSidebarPanelComponent } from '@features/purchase/supplier_credit_notes/supplier-credit-note-details/supplier-credit-note-sidebar-panel/supplier-credit-note-sidebar-panel.component';
import { SupplierCreditNoteCoordinator } from '@features/purchase/supplier_credit_notes/supplier-credit-note-details/supplier-credit-note-setup-and-helpers/SupplierCreditNoteCoordinator/supplier-credit-note.coordinator';
import { supplierCreditNoteRefreshPlanBuilder } from '@features/purchase/supplier_credit_notes/supplier-credit-note-details/supplier-credit-note-setup-and-helpers/SupplierCreditNoteRefreshPlanBuilder/supplier-credit-note.refresh-plan-builder';
import { Router } from '@angular/router';
import { CreateSupplierComplaintModalComponent } from '@features/purchase/supplier-invoices/supplier-invoice-detail/create-supplier-complaint-modal/create-supplier-complaint-modal.component';

const LINKED_DOCUMENT_FIELDS = ['clientInvoices', 'supplierInvoices', 'clientCreditNotes'] as const;
type LinkedDocumentField = (typeof LINKED_DOCUMENT_FIELDS)[number];

const LINKED_DOCUMENT_FIELD_BY_TYPE: Partial<Record<SupplierCreditNoteMappingType, LinkedDocumentField>> = {
  [SupplierCreditNoteMappingType.CLIENT_INVOICE]: 'clientInvoices',
  [SupplierCreditNoteMappingType.SUPPLIER_INVOICE]: 'supplierInvoices',
  [SupplierCreditNoteMappingType.CLIENT_CREDIT_NOTE]: 'clientCreditNotes',
};

@Component({
  selector: 'rc-supplier-credit-note-details',
  imports: [
    DocumentWrapperComponent,
    BreadcrumbsComponent,
    CopyableTitleComponent,
    OverviewCreateButtonComponent,
    ButtonComponent,
    CapitalizePipe,
    FormFieldComponent,
    FormsModule,
    LabelComponent,
    ReactiveFormsModule,
    TextAreaComponent,
    TranslateModule,
    UpperCasePipe,
    NgTemplateOutlet,
    ValidationDirective,
    SupplierCreditNoteItemComponent,
    SupplierCreditNoteTotalsAndTaxesComponent,
    SupplierCreditNoteSidebarPanelComponent,
    TableModalComponent,
  ],
  providers: [CapitalizePipe, FormErrorService, TranslatePipe, NavigationService, DialogRef],
  templateUrl: './supplier-credit-note-details.component.html',
  styleUrl: './supplier-credit-note-details.component.scss',
})
export class SupplierCreditNoteDetailsComponent extends DocumentWrapperComponent<
  SupplierCreditNote,
  SupplierCreditNoteItem
> {
  #dialog = inject(DialogRef);
  #capitalizePipe = inject(CapitalizePipe);
  #translateService = inject(TranslateService);
  #formErrorService = inject(FormErrorService);
  #navigationService = inject(NavigationService);
  #menu = inject(RouteContextMenuService);
  #notificationService = inject(NotificationService);
  #tracker = inject(RouteEntitySubscriberService);
  #translationService = inject(TranslateService);
  #supplierCreditNoteService = inject(SupplierCreditNoteService);
  #clientCreditNoteService = inject(ClientCreditNoteService);
  #coordinator = inject(SupplierCreditNoteCoordinator);
  #router = inject(Router);
  #detailTitleService = inject(DetailTitleService);

  modals = viewChild(TableModalComponent);

  supplierCreditNote = this.#tracker.getSignal<SupplierCreditNote>('supplierCreditNote');
  supplierCreditNoteItems = this.#tracker.getSectionItemsSignal<SupplierCreditNoteItem>('supplierCreditNote');
  supplierCreditNoteItemMappings =
    this.#tracker.getItemMappingsSignal<SupplierCreditNoteItemMapping>('supplierCreditNote');
  clientCreditNoteDraftItems = this.#tracker.getDraftItemsSignal<ClientCreditNoteDraftItem>('supplierCreditNote');
  clientCreditNoteItems = this.#tracker.getLinkedItemsSignal<ClientCreditNoteItem>('supplierCreditNote');

  wrapperItems = computed(() => this.supplierCreditNoteItems()?.[this.supplierCreditNote()?.['@id']!] ?? []);
  supplierCreditNoteIsLocked = computed(() => this.supplierCreditNote()?.status === SupplierCreditNoteStatus.PROCESSED);

  allClientCreditNoteDraftItems = computed(() => {
    const draftItems = this.clientCreditNoteDraftItems();
    const creditNoteId = this.supplierCreditNote()?.['@id']!;
    return draftItems?.[creditNoteId] ?? [];
  });

  mappingsByItem = computed(() => {
    const result = new Map<string, SupplierCreditNoteItemMapping[]>();
    const mappings: DocumentSections<SupplierCreditNoteItemMapping> | undefined = this.supplierCreditNoteItemMappings();

    if (mappings) {
      for (const m of mappings?.[this.supplierCreditNote()?.['@id']!]) {
        const itemIri = m.supplierCreditNoteItem;
        if (!itemIri) continue;
        const arr = result.get(itemIri) ?? [];
        arr.push(m);
        result.set(itemIri, arr);
      }
    }

    return result;
  });

  draftItemsByItem = computed(() => {
    const result = new Map<string, ClientCreditNoteDraftItem[]>();
    const draftItemsSections = this.clientCreditNoteDraftItems();
    const mappingsSections = this.supplierCreditNoteItemMappings();

    if (!draftItemsSections || !mappingsSections) return result;

    // iterate all supplier credit note items
    const creditNoteId = this.supplierCreditNote()?.['@id']!;
    const mappingsForNote = mappingsSections[creditNoteId] ?? [];

    for (const mapping of mappingsForNote) {
      const itemIri = mapping.supplierCreditNoteItem;
      if (!itemIri) continue;

      const draftIri = mapping.clientCreditNoteDraftItem;
      if (!draftIri) continue;

      // find the full draft item object by IRI
      let draftItem: ClientCreditNoteDraftItem | undefined;
      for (const drafts of Object.values(draftItemsSections)) {
        draftItem = drafts.find((d) => d['@id'] === draftIri);
        if (draftItem) break;
      }
      if (!draftItem) continue;

      const arr = result.get(itemIri) ?? [];
      arr.push(draftItem);
      result.set(itemIri, arr);
    }

    return result;
  });

  linkedItemsByItem = computed(() => {
    const result = new Map<string, ClientCreditNoteItem[]>();
    const linkedItemsSections = this.clientCreditNoteItems();
    const mappingsSections = this.supplierCreditNoteItemMappings();

    if (!linkedItemsSections || !mappingsSections) return result;

    // iterate all supplier credit note items
    const creditNoteId = this.supplierCreditNote()?.['@id']!;
    const mappingsForNote = mappingsSections[creditNoteId] ?? [];

    for (const mapping of mappingsForNote) {
      const itemIri = mapping.supplierCreditNoteItem;
      if (!itemIri) continue;

      const linkedIri = mapping.clientCreditNoteItem;
      if (!linkedIri) continue;

      // find the full linked item object by IRI
      let linkedItem: ClientCreditNoteItem | undefined;
      for (const linkedItems of Object.values(linkedItemsSections)) {
        linkedItem = linkedItems.find((l) => l['@id'] === linkedIri);
        if (linkedItem) break;
      }
      if (!linkedItem) continue;

      const arr = result.get(itemIri) ?? [];
      arr.push(linkedItem);
      result.set(itemIri, arr);
    }

    return result;
  });

  amountOfClientCreditNotesToBeCreated = computed(() => {
    const mappings = this.supplierCreditNoteItemMappings();
    const creditNoteId = this.supplierCreditNote()?.['@id'];

    if (!mappings || !creditNoteId) return 0;

    const clients = new Set<string>();

    for (const m of mappings[creditNoteId] ?? []) {
      if (m.client) clients.add(m.client);
    }

    return clients.size;
  });

  clientNames = computed(() => {
    const mappings = this.supplierCreditNoteItemMappings();
    const creditNoteId = this.supplierCreditNote()?.['@id'];
    if (!mappings || !creditNoteId) return [];

    const names = new Set<string>();
    for (const m of mappings[creditNoteId] ?? []) {
      if (m.clientName) names.add(m.clientName);
    }

    return [...names];
  });

  isCreditNoteFullyMapped = computed(() => {
    const items = this.wrapperItems();
    return items.filter((i) => !i.doNotCredit).every((i) => i.supplierCreditNoteItemMappings.length > 0);
  });

  readonly titleParts = this.#detailTitleService.titleParts;
  state = signal<ListContext | null>(null);

  supplierCreditNoteForm: FormGroup<SupplierCreditNoteForm> = this.#initForm();
  private formValues = toSignal(this.supplierCreditNoteForm.valueChanges, {
    initialValue: this.supplierCreditNoteForm.getRawValue(),
  });
  notesBadgeCount = computed(() => [this.formValues().internalNote].filter((v) => !!v?.trim()).length);
  subscribedEntities = new Set<string>();
  #watchedNoteIri?: string;
  #formNoteIri?: string;

  // Field changes are staged per credit note IRI (the component is reused across previous/next) and sent
  // as one merge-patch, so concurrent edits of different fields can't drop each other.
  #pendingChanges = new Map<string, Partial<SupplierCreditNote>>();
  #inFlightChanges = new Map<string, Partial<SupplierCreditNote>>();
  #saveRequested$ = new Subject<void>();
  #saveNow$ = new Subject<void>();
  #saved$ = new Subject<{ iri: string; fields: string[]; ok: boolean }>();
  #refreshEntity$ = new Subject<string>();

  tableConfig: TableConfig<ClientInvoice | SupplierInvoice | ClientCreditNote> = {
    entityTranslationKey: '',
    url: '',
    subscribeToItems: false,
    pageable: false,
    sortable: false,
    filterable: false,
    colAmount: 7,
  };

  constructor() {
    super();
    // Drop the previous page's title so the header shows its skeleton until this one loads.
    this.titleParts.set([]);

    this.returnParams = this.#navigationService.getLastQueryParams();

    effect(() => {
      const noteIri = this.supplierCreditNote()?.['@id'];
      const list = this.#allEntitiesToWatch();

      // The tracker drops item subscriptions when switching notes, so forget what we subscribed for the old one.
      if (noteIri !== this.#watchedNoteIri) {
        this.#watchedNoteIri = noteIri;
        this.subscribedEntities.clear();
      }

      const fresh = list.filter((e) => e?.['@id'] && !this.subscribedEntities.has(e['@id']));
      if (!fresh.length) return;

      fresh.forEach((e) => this.subscribedEntities.add(e['@id']!));
      this.#tracker.subscribeToItems('supplierCreditNote', fresh);
    });

    // Only depends on the credit note itself, so item/mapping changes don't re-patch the form while the user types.
    effect(() => {
      const creditNote = this.supplierCreditNote();
      if (!creditNote) return;

      untracked(() => {
        this.state.set(this.#navigationService.getListContext());
        this.#setBreadCrumbs();
        this.titleParts.set([
          { text: creditNote.documentNumber, copyable: true },
          { text: creditNote.supplierName, copyable: false },
        ]);

        this.detailActionService.status.set(
          resolveStatusEntity(creditNote.status, SUPPLIER_CREDIT_NOTE_STATUS_CONFIG, this.#translationService),
        );

        this.detailActionService.actionMenuItems.set(this.#getMenuItems());
        this.#syncForm(creditNote);
      });
    });

    effect(() => {
      const creditNote = this.supplierCreditNote();
      if (!creditNote) return;

      if (this.supplierCreditNoteIsLocked()) {
        this.detailActionService.creationButton.set(undefined);
        return;
      }

      const amount = this.amountOfClientCreditNotesToBeCreated();
      const disabled = !this.isCreditNoteFullyMapped() || creditNote.doNotCredit;
      untracked(() => {
        const buttonText = this.capitalizePipe.transform(
          this.translationService.instant('CREATE_ENTITY_ACTION', {
            entity: this.translationService.instant('CREDIT_NOTES'),
          }),
        );
        this.detailActionService.creationButton.set({
          customText: true,
          text: amount > 0 ? `${buttonText} (${amount})` : buttonText,
          iconClass: 'rc-icon-check',
          action: () => this.#createClientCreditNote(),
          disabled,
        });
      });
    });

    // concatMap (not switchMap): never cancel a PATCH that is already on its way.
    merge(this.#saveRequested$.pipe(debounceTime(600)), this.#saveNow$)
      .pipe(concatMap(() => this.#flushPendingChanges()))
      .subscribe();

    // switchMap: only the latest refresh may write the entity, so a slow older response can't overwrite newer data.
    this.#refreshEntity$
      .pipe(switchMap((iri) => this.#supplierCreditNoteService.get(iri).pipe(catchError(() => EMPTY))))
      .subscribe((creditNote) => {
        if (creditNote['@id'] === this.supplierCreditNote()?.['@id']) {
          this.#tracker.setSignal('supplierCreditNote', creditNote);
        }
      });

    const plan = supplierCreditNoteRefreshPlanBuilder({ tracker: this.#tracker, coordinator: this.#coordinator });
    this.#tracker.registerSignalTopicBinding('supplierCreditNote', plan);
  }

  updateSupplierCreditNote(field: keyof SupplierCreditNoteForm) {
    if (this.#stageChanges({ [field]: this.supplierCreditNoteForm.controls[field].value ?? '' })) {
      this.#saveRequested$.next();
    }
  }

  updateSupplierCreditNoteItems(itemIri: string | boolean) {
    const creditNoteIri = this.supplierCreditNote()?.['@id'];
    if (!creditNoteIri) return;

    // The coordinator refetches items, mappings, drafts and linked items together; a separate items fetch here
    // raced it and whichever response came last won.
    this.#coordinator.refreshCreditNote();
    this.#coordinator.refreshCreditNoteEntity();
    this.#tracker.broadcastItemTopicChanged(
      'supplierCreditNote',
      typeof itemIri === 'string' ? itemIri : creditNoteIri,
    );
    this.#notifySuccess('SUPPLIER_CREDIT_NOTE');
  }

  linkDocument() {
    const storedLink = localStorage.getItem('supplierCreditNoteLink');
    if (!storedLink) {
      // this.#notifyError('NO_DOCUMENT_FOUND_TO_LINK');
      return;
    }

    const link = JSON.parse(storedLink) as StoredSupplierCreditNoteItemLink;
    const field = LINKED_DOCUMENT_FIELD_BY_TYPE[link.documentType];

    if (!field) {
      // keep it copied: it can still be pasted where it fits
      this.#notifyError('NO_DOCUMENT_FOUND_TO_LINK');
      return;
    }

    const noteIri = this.supplierCreditNote()?.['@id'];
    const current = this.#currentLinkedDocuments(field);
    if (!noteIri) return;
    if (current.includes(link.documentIri)) {
      localStorage.removeItem('supplierCreditNoteLink');
      return;
    }
    this.#stageChanges({ [field]: [...current, link.documentIri] });

    // Only a saved link uses up the copied link; a failed save keeps it for another try.
    this.#saved$
      .pipe(
        filter((saved) => saved.iri === noteIri && saved.fields.includes(field)),
        take(1),
      )
      .subscribe((saved) => {
        if (saved.ok && localStorage.getItem('supplierCreditNoteLink') === storedLink) {
          localStorage.removeItem('supplierCreditNoteLink');
        }
      });

    this.#saveRequested$.next();
  }

  updateTotals() {
    const iri = this.supplierCreditNote()?.['@id'];
    if (!iri) return;

    this.#refreshEntity$.next(iri);
    // Extra costs are mirrored as credit note lines by the backend, so items/mappings change too.
    this.#coordinator.refreshCreditNote();
    this.#tracker.broadcastItemTopicChanged('supplierCreditNote', iri);
  }

  #createClientCreditNote() {
    const clientCreditNote = {
      supplierCreditNote: this.supplierCreditNote()!['@id']!,
      items: this.allClientCreditNoteDraftItems().map((item) => item['@id']!),
    };
    this.#clientCreditNoteService
      .createFromSupplierCreditNote(clientCreditNote as unknown as ClientCreditNote)
      .pipe(take(1), distinctUntilChanged())
      .subscribe({
        next: (response) => {
          this.#notifySuccess('CLIENT_CREDIT_NOTE');
          this.#coordinator.refreshCreditNoteEntity();
          this.#coordinator.refreshCreditNote();
          this.#tracker.broadcastItemTopicChanged('supplierCreditNote', this.supplierCreditNote()!['@id']!);
          if (response.clientCreditNotes.length > 0) {
            const tree = this.#router.createUrlTree([`sales/client_credit_notes`]);
            const url = `${this.router.serializeUrl(tree)}?supplierCreditNotes=${this.supplierCreditNote()!['@id']}`;
            window.open(url, '_blank', 'noopener');
          }
        },
        error: (err: HttpErrorResponse) => this.#notifyError(err),
      });
  }

  openClientInvoiceLinksModal() {
    this.#setupTableConfigForModal('client_invoice');
    const modal = this.modals()?.clientInvoiceModal();
    if (modal) {
      this.#dialog = this.dialogService.open({
        content: modal,
        title: this.#capitalizePipe.transform(
          this.#translateService.instant('LINKED_ENTITY', {
            entity: this.#translateService.instant('CLIENT_INVOICES'),
          }),
        ),
        width: '1200px',
      });
    }
  }

  openSupplierInvoiceLinksModal() {
    this.#setupTableConfigForModal('supplier_invoice');
    const modal = this.modals()?.supplierInvoiceModal();
    if (modal) {
      this.#dialog = this.dialogService.open({
        content: modal,
        title: this.#capitalizePipe.transform(
          this.#translateService.instant('LINKED_ENTITY', {
            entity: this.#translateService.instant('SUPPLIER_INVOICES'),
          }),
        ),
        width: '1200px',
      });
    }
  }

  openClientCreditNoteLinksModal() {
    this.#setupTableConfigForModal('client_credit_note');
    const modal = this.modals()?.clientCreditNoteModal();
    if (modal) {
      this.#dialog = this.dialogService.open({
        content: modal,
        title: this.#capitalizePipe.transform(
          this.#translateService.instant('LINKED_ENTITY', {
            entity: this.#translateService.instant('CREDIT_NOTES'),
          }),
        ),
        width: '1200px',
      });
    }
  }

  #setupTableConfigForModal(entityKey: string) {
    const pluralEntityKey = entityKey + 's';
    this.tableConfig.entityTranslationKey = pluralEntityKey.toUpperCase();
    this.tableConfig.url = `/${pluralEntityKey}?supplierCreditNotes=${this.supplierCreditNote()!['@id']!}`;
    this.tableConfig.actionMenuItems$ = this.#getModalActionMenuItems(entityKey.toUpperCase());
    this.tableConfig.onActionMenuItemClick = (menuItem, record, propField) => {
      this.#onModalActionMenuClick(menuItem, record, propField + 's');
    };
  }

  #removeLinkedDocument(iri: string, listField: LinkedDocumentField) {
    const noteIri = this.supplierCreditNote()?.['@id'];
    const current = this.#currentLinkedDocuments(listField);
    if (!noteIri || !current.includes(iri)) return;

    if (!this.#stageChanges({ [listField]: current.filter((linked) => linked !== iri) })) return;

    // reload modal table only after the update for this field is saved
    this.#saved$
      .pipe(
        filter((saved) => saved.iri === noteIri && saved.fields.includes(listField)),
        take(1),
      )
      .subscribe((saved) => {
        if (saved.ok) this.modals()?.table()?.reload();
      });

    this.#saveNow$.next();
  }

  /** Stages field changes for the current credit note; returns false when no credit note is loaded. */
  #stageChanges(changes: Partial<SupplierCreditNote>): boolean {
    const iri = this.supplierCreditNote()?.['@id'];
    if (!iri) return false;

    this.#pendingChanges.set(iri, { ...this.#pendingChanges.get(iri), ...changes });
    return true;
  }

  /** Newest known value of a linked-documents list: staged, then in flight, then the loaded entity. */
  #currentLinkedDocuments(field: LinkedDocumentField): string[] {
    const creditNote = this.supplierCreditNote();
    const iri = creditNote?.['@id'];
    if (!iri) return [];

    return this.#pendingChanges.get(iri)?.[field] ?? this.#inFlightChanges.get(iri)?.[field] ?? creditNote[field] ?? [];
  }

  #hasUnsavedChange(iri: string, field: keyof SupplierCreditNote): boolean {
    return field in (this.#pendingChanges.get(iri) ?? {}) || field in (this.#inFlightChanges.get(iri) ?? {});
  }

  #flushPendingChanges(): Observable<unknown> {
    const batches = [...this.#pendingChanges.entries()];
    this.#pendingChanges.clear();
    if (!batches.length) return EMPTY;

    return from(batches).pipe(concatMap(([iri, changes]) => this.#saveChanges(iri, changes)));
  }

  #saveChanges(iri: string, changes: Partial<SupplierCreditNote>): Observable<SupplierCreditNote> {
    const fields = Object.keys(changes);
    this.#inFlightChanges.set(iri, changes);

    return this.#supplierCreditNoteService.update(iri, changes as SupplierCreditNote).pipe(
      tap((saved) => {
        this.#inFlightChanges.delete(iri);
        if (saved['@id'] === this.supplierCreditNote()?.['@id']) {
          this.#tracker.setSignal('supplierCreditNote', saved);
        }
        if (fields.some((field) => (LINKED_DOCUMENT_FIELDS as readonly string[]).includes(field))) {
          this.#tracker.broadcastItemTopicChanged('supplierCreditNote', iri);
        }
        this.#notifySuccess('SUPPLIER_CREDIT_NOTE');
        this.#saved$.next({ iri, fields, ok: true });
      }),
      catchError((err: HttpErrorResponse) => {
        this.#inFlightChanges.delete(iri);
        this.#notifyError(err);
        this.#formErrorService.setErrorViolations(err);
        this.#saved$.next({ iri, fields, ok: false });
        return EMPTY;
      }),
    );
  }

  #setBreadCrumbs() {
    this.breadcrumbService.loading = true;
    const params = { ...this.returnParams };
    this.breadcrumbService.breadcrumbsSubject.next([
      {
        label: 'SUPPLIER_CREDIT_NOTES',
        path: 'purchase/supplier_credit_notes',
        params: params,
        extraData: { state: { isRedirect: true } },
      },
    ]);
    this.breadcrumbService.loading = false;
  }

  #getMenuItems() {
    const actionMenuItems: ButtonActionEntity[] = [];

    actionMenuItems.push({
      text: this.#capitalizePipe.transform(this.#translateService.instant('COMPOSE_COMPLAINT')),
      iconClass: 'rc-icon-check',
      action: () => this.#openCreateSupplierComplaintDialog(),
    });

    return actionMenuItems;
  }

  #openCreateSupplierComplaintDialog() {
    this.dialog = this.dialogService.open({
      content: CreateSupplierComplaintModalComponent,
      title: this.capitalizePipe.transform(
        this.translationService.instant('GENERATE_ENTITY', {
          entity: this.translationService.instant('SUPPLIER_COMPLAINT'),
        }),
      ),
      width: 1200,
    });

    this.dialog.content.instance.supplierCreditNote.set(this.supplierCreditNote());
  }

  #initForm(): FormGroup<SupplierCreditNoteForm> {
    return new FormGroup<SupplierCreditNoteForm>({
      internalNote: new FormControl('', {
        validators: [],
      }),
    });
  }

  #patchForm(supplierCreditNote: SupplierCreditNote) {
    this.supplierCreditNoteForm.patchValue({
      internalNote: supplierCreditNote.internalNote ?? '',
    });
  }

  /** Patches the form from the entity, unless the user has an unsaved note for this same credit note. */
  #syncForm(creditNote: SupplierCreditNote) {
    const iri = creditNote['@id'];
    if (iri && iri === this.#formNoteIri && this.#hasUnsavedChange(iri, 'internalNote')) return;

    this.#formNoteIri = iri;
    this.#patchForm(creditNote);
  }

  #allEntitiesToWatch(): Entity[] {
    const out: Entity[] = [];

    const cn = this.supplierCreditNote();
    if (cn?.['@id']) out.push(cn as unknown as Entity);

    const items = Object.values(this.supplierCreditNoteItems() ?? {}).flat() ?? [];
    items.forEach((item) => item?.['@id'] && out.push(item as unknown as Entity));

    const mappings = Object.values(this.supplierCreditNoteItemMappings() ?? {}).flat() ?? [];
    mappings.forEach((mapping) => mapping?.['@id'] && out.push(mapping as unknown as Entity));

    const draftItems = Object.values(this.clientCreditNoteDraftItems() ?? {}).flat() ?? [];
    draftItems.forEach((draftItem) => draftItem?.['@id'] && out.push(draftItem as unknown as Entity));

    const linkedItems = Object.values(this.clientCreditNoteItems() ?? {}).flat() ?? [];
    linkedItems.forEach((linkedItem) => linkedItem?.['@id'] && out.push(linkedItem as unknown as Entity));

    return out;
  }

  #getModalActionMenuItems(entityTranslationKey: string): Observable<ActionListItemModel<ActionMenuItemAction>[]> {
    return of([
      {
        text: this.#capitalizePipe.transform(
          this.#translateService.instant('UNLINK_ENTITY', {
            entity: this.#translateService.instant(entityTranslationKey),
          }),
        ),
        action: ActionMenuItemAction.DISASSOCIATE,
        iconClass: 'rc-icon-link-off',
      },
    ]);
  }

  #onModalActionMenuClick(
    actionMenuItem: ActionListItemModel<ActionMenuItemAction | string>,
    record: string,
    propField: string,
  ) {
    switch (actionMenuItem.action) {
      case ActionMenuItemAction.DISASSOCIATE:
        this.#removeLinkedDocument(record, propField as LinkedDocumentField);
        break;
    }
  }

  close() {
    this.#dialog.close();
  }

  previous() {
    if (this.state() && this.state()!.index > 0) {
      const listContext = {
        ...this.state()!,
        index: this.state()!.index - 1,
      };
      this.#navigationService.setListContext(listContext);
      this.router.navigate([`/purchase${listContext.idsOnPage[listContext.index]}`], {
        state: {
          listContext: listContext,
        },
      });
    }
  }

  next() {
    if (this.state()!.index < this.state()!.idsOnPage.length - 1) {
      const listContext = {
        ...this.state()!,
        index: this.state()!.index + 1,
      };
      this.#navigationService.setListContext(listContext);
      this.router.navigate([`/purchase${listContext.idsOnPage[listContext.index]}`], {
        state: {
          listContext: listContext,
        },
      });
    }
  }

  cancelButton() {
    this.router.navigate(['/purchase/supplier_credit_notes'], {
      queryParams: this.returnParams,
      state: { isRedirect: true },
    });
  }

  onOverviewContextMenu(event: MouseEvent): void {
    event.preventDefault();
    this.#menu.openMenu(['purchase/supplier_credit_notes'], event.clientX, event.clientY, {
      queryParams: this.returnParams,
    });
  }

  onAuxClick(event: PointerEvent): void {
    event.preventDefault();
    this.#menu.openInNewTab(['purchase/supplier_credit_notes'], { queryParams: this.returnParams });
    return;
  }

  #notifySuccess(message: string, plural = false) {
    const n = this.#notificationService.show(successSaveToast);
    if (n.content) {
      n.content.instance.value = message;
      (n.content.instance as any).plural = plural;
    }
  }

  #notifyError(error: any) {
    const n = this.#notificationService.show(errorToast);
    if (n.content) n.content.instance.setCurrentValue(error);
  }
}

interface SupplierCreditNoteForm {
  internalNote: FormControl<string | null>;
}
